import PageLayout from "Layouts/PageLayout";
import {useTranslation} from "react-i18next";
import CalculatorIcon from "../../Assets/SVG/CalculatorIcon";
import Separator from "Components/Separator";
import React, {useCallback, useEffect, useMemo, useState} from "react";
import {SettlementEntryDto} from "../../Apis/Models/SettlementSalesDto";
import Label from "../../Components/Label";
import Select from "../../Components/Select";
import {IQueryParam} from "../../Apis/Models/IQueryParams";
import useFilteredList from "../../Hooks/UseFilteredList";
import useQueryParams from "../../Hooks/UseQueryParams";
import DatePicker from "../../Components/DatePicker";
import moment from "moment";
import Button from "../../Components/Button";
import HandsIcon from "../../Assets/SVG/HandsIcon";
import Modal from "../../Components/Modal";
import SettlementEntryForm from "../../Features/Settlements/SettlementEntryForm";
import {TotalComponentV2} from "../../Components/TotalComponent";
import {formatNumber} from "../../Utils/NumberUtils";
import MoneyIcon from "Assets/SVG/MoneyIcon";
import styles from "./SettlementResult.module.scss";
import Space from "../../Components/Space";
import {
    useSettlementProjects,
    useSettlementResult,
    useSettlementSales,
    useSettlePeriod,
    useDownloadSettlementPdf, getSettlementCSV
} from "../../Apis/ProjectSettlementApi";
import SettlementStatementBox from "../../Features/Settlements/SettlementStatementBox";
import {useUserContext} from "../../Providers/UserContextProvider";
import {canAccess} from "../../Utils/CanAccess";
import { saveAs } from 'file-saver';
import useToasts from "../../Hooks/UseToasts";
import {useImportShopifyOrderByPeriod} from "../../Apis/ShopifyOrderApi";
import Fireworks from "react-canvas-confetti/dist/presets/fireworks";
import Loader from "../../Assets/SVG/Loader";
import FlexRow from "../../Components/FlexRow";
import _ from "lodash";

const SettlementResult = () => {

    const [lastSaleAt, setLastSaleAt] = useState<Date | null>(null);
    const [showFireWorks, setShowFireWorks] = useState<boolean>(false)
    const { t } = useTranslation();
    
    const { user } = useUserContext();
    
    const { showErrorToast, showSuccessToast } = useToasts();
    
    const isAdmin = canAccess(user.permissions, 'Admin')

    const [ modalVisible, setModalVisible ] = useState<boolean>(false);
    const [ isDownloadingCsv, setIsDownloadingCsv ] = useState<boolean>(false);
    const [ isSetteling, setIsSetteling ] = useState<boolean>(false);
    const [ selectedSettlementEntry, setSelectedSettlementEntry ] = useState<SettlementEntryDto | undefined>(undefined);
    
    const { data: projects } = useSettlementProjects();

    const [ filter, setFilter ] = useQueryParams();

    const { data: settlementResult, isLoading } = useSettlementResult(filter, filter['includeUnfulfilled'] === 'true' ? 10000 : undefined);
    const { data: settlementSales } = useSettlementSales(filter);
    const { mutateAsync: settle } = useSettlePeriod();
    const { mutateAsync: downloadSettlement } = useDownloadSettlementPdf();
    const { mutateAsync: reimportShopifyOrders, isLoading: isReimporting } = useImportShopifyOrderByPeriod();

    const getCurrency = () => {
        return settlementResult?.currency ?? "DKK"
    }

    useEffect(() => {
        if (!!settlementResult?.latestSaleAt) {
            const newLastSale = moment(settlementResult.latestSaleAt).toDate();

            if (lastSaleAt == null) {
                setLastSaleAt(newLastSale);
            } else if (newLastSale !== lastSaleAt) {
                setLastSaleAt(newLastSale);
                setShowFireWorks(true);
                showSuccessToast(t('newOrderReceived'))
                setTimeout(() => {
                    setShowFireWorks(false)
                }, 3000)
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [settlementResult?.latestSaleAt]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const checkProjectId = useCallback(_.debounce((currentFilter, projects) => {
        console.log(currentFilter['projectId'])
        if (!currentFilter['projectId'] && !!projects && projects.length > 0) {
            setFilter({projectId: projects[0].id, ...currentFilter});
        }
    }, 500), [])

    useEffect(() => {
        if (!!projects && projects.length > 0) {
            setTimeout(() => {
                checkProjectId(filter, projects)
            }, 300)
        }
    }, [projects, filter, checkProjectId]);

    const includeUnfulfilled = useMemo(() => [
        { value: 'false', label: t('no')},
        { value: 'true', label: t('yes')},
    ], [t]);

    const groupByProduct = useMemo(() => [
        { value: undefined, label: t('groupByVariant')},
        { value: 'true', label: t('groupByProduct')},
    ], [t]);
    
    const [ filteredProjects, setProjectFilter, projectFilterValue ] = useFilteredList(projects ?? [], x => [x.name]);
    const [ filteredUnfulfilled, setFilteredUnfulfilled ] = useFilteredList(includeUnfulfilled, x => [x.label]);
    const [ filteredGroupBy, setFilteredGroupBy ] = useFilteredList(groupByProduct, x => [x.label]);

    const setQueryField = (fieldName: 'from' | 'to' | 'projectId' | 'includeUnfulfilled' | 'groupByProduct', value: IQueryParam) => {
        setLastSaleAt(null)
        const newValue = {...filter, [fieldName]: value }
        setFilter(newValue, true);
    }
    
    const closeManualEntryModal = () => {
        setModalVisible(false);
        setSelectedSettlementEntry(undefined)
    }
    
    const selectEntry = (entry: SettlementEntryDto) => {
        if (!isAdmin) {
            return;
        }

        setSelectedSettlementEntry(entry);
        setModalVisible(true);
    }
    
    const settlePeriod = async () => {
        setIsSetteling(true);
        
        try {
            const response = await settle(filter);

            const responseBlob = await downloadSettlement(response.id)

            const blob = new Blob([responseBlob], {type: 'application/pdf' });

            const project = projects!.filter(x => x.id === filter['projectId'])[0];

            saveAs(blob, `${project.name} - ${filter['from']} / ${filter['to']}.pdf`);

            showSuccessToast(t('periodSettled'));
        }
        catch (e) {
            showErrorToast(t('couldNotSettlePeriod'));
            console.error(e);
        }
        finally {
            setIsSetteling(false);
        }
    }
    
    const downloadSettlementCSV = async () => {
        setIsDownloadingCsv(true);
        
        try {
            const project = projects!.filter(x => x.id === filter['projectId'])[0];

            await getSettlementCSV(filter, `Opgørelse | ${project.name} | ${filter['from']} / ${filter['to']}.csv`)       
        }
        finally {
            setIsDownloadingCsv(false);
        }
    }
    
    const reimport = async () => {
        await reimportShopifyOrders({
            from: filter['from'] as string,
            to: filter['to'] as string,
            projectId: filter['projectId'] as number
        });
        
        showSuccessToast(t('ordersQueuedForReimport'));
    }
    
    const settlementItemsByText = (entryType: 'Expense' | 'Revenue') => {
        const revenueItems = settlementResult?.settlementEntries.filter(x => x.entryType === entryType);

        const revenueTexts = new Set(revenueItems?.map(x => x.text.toLowerCase()));
        
        return Array.from(revenueTexts).map(revenueText => {
            const items = revenueItems?.filter(x => x.text.toLowerCase() === revenueText)!;
            
            return {
                text: items[0].text,
                quantity: items?.length,
                amount: items?.reduce((a, b) => a + b.amount, 0),
                items: items,
            }
        })
    }
    
    return (
        <>
            {showFireWorks && (
                <Fireworks autorun={{speed: 1, duration: 2000}}/>
            )}
            <PageLayout title={t('settlements')} icon={<CalculatorIcon/>} header={(
                <>
                    {isAdmin && (
                        <Space size={"small"} direction={"horizontal"}>
                            <Button prefix={<HandsIcon fill={"#FFF"} />} text={t('reimportPeriod')} loading={isReimporting} type={"secondary"} onClick={reimport} />
                            <Button prefix={<HandsIcon fill={"#FFF"} />} text={t('downloadCSV')} loading={isDownloadingCsv} type={"secondary"} onClick={downloadSettlementCSV} />
                            <Button prefix={<HandsIcon fill={"#FFF"} />} text={t('createEntry')} type={"secondary"} onClick={() => setModalVisible(true)} />
                            <Button prefix={<HandsIcon fill={"#FFF"} />} text={t('settle')} loading={isSetteling} type={"primary"} onClick={settlePeriod} />
                        </Space>
                    )}
                </>
            )}>
                <div>

                    <div style={{display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 20}}>

                        <div>
                            <Label placeholder={t('from')} />
                            <DatePicker name={'from'} onChange={value => setQueryField('from', value ? moment(value).format('YYYY-MM-DD') : '')} value={!!filter['from'] ? filter['from'].toString() : undefined} />
                        </div>

                        <div>
                            <Label placeholder={t('to')} />
                            <DatePicker name={'to'} onChange={value => setQueryField('to', value ? moment(value).format('YYYY-MM-DD') : '')} value={!!filter['to'] ? filter['to'].toString() : undefined} />
                        </div>

                        <div>
                            <Label placeholder={t('project')}/>
                            <Select name={"projectId"} value={filter['projectId']} onChange={value => setQueryField('projectId', value)} onFilter={setProjectFilter}>
                                {!projectFilterValue && (
                                    <Select.Option value={undefined} label={t('select')}/>
                                )}
                                {filteredProjects.map(project => <Select.Option key={project.id} value={project.id} label={project.name}/>)}
                            </Select>
                        </div>

                        <div>
                            <Label placeholder={t('showUnfulfilled')}/>
                            <Select name={"includeUnfulfilled"} value={filter['includeUnfulfilled']} onChange={value => setQueryField('includeUnfulfilled', value)} onFilter={setFilteredUnfulfilled}>
                                {filteredUnfulfilled?.map(project => <Select.Option key={project.value} value={project.value} label={project.label}/>)}
                            </Select>
                        </div>

                        <div>
                            <Label placeholder={t('groupByProduct')}/>
                            <Select name={"groupByProduct"}  value={filter['groupByProduct']} onChange={value => setQueryField('groupByProduct', value)} onFilter={setFilteredGroupBy}>
                                {filteredGroupBy?.map(project => <Select.Option key={project.value} value={project.value} label={project.label}/>)}
                            </Select>
                        </div>
                    </div>

                    <Separator style={{margin: '30px 0'}}/>
                        <div style={filter['includeUnfulfilled'] === 'true' ? {color: 'red'} : {}}>
                            {filter['includeUnfulfilled'] === 'true' ? t('allPricesAreExclVatMessageUnfulfilled') : t('allPricesAreExclVatMessage')}
                        </div>
                    <Separator style={{margin: '30px 0'}}/>



                    {(isLoading) && (
                        <FlexRow justify={"center"} align={"center"} className={"h-100"}>
                            <Loader fill={"#e91d25"} width={100} height={100}/>
                        </FlexRow>
                    )}

                    {!!settlementResult && (
                        <div style={{display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 20}}>
                            <Space size={"large"}>
                                <Space>
                                    <div className={`${styles.statementGrid}`}>
                                            <span>
                                                {t('revenue')}
                                            </span>
                                        <span>
                                                {t('attachment')}
                                            </span>
                                        <span>
                                                {t('quantity')}
                                            </span>
                                        <span>
                                                {t('total')}
                                            </span>
                                    </div>
                                    <SettlementStatementBox text={t('weborders')} quantity={settlementResult.orderCountWithoutB2B} amount={settlementResult.totalRevenueExclB2B} onSelect={selectEntry} isAdmin={isAdmin} currency={getCurrency()} />
                                    <SettlementStatementBox text={t('b2bOrders')} quantity={settlementResult.orderCountB2B} amount={settlementResult.totalRevenueWithB2B} onSelect={selectEntry} isAdmin={isAdmin} currency={getCurrency()} />

                                    {settlementItemsByText('Revenue').map(x =>
                                        <SettlementStatementBox key={x.text} text={x.text} quantity={x.quantity} amount={x.amount} items={x.items} onSelect={selectEntry}  isAdmin={isAdmin} currency={getCurrency()}/>
                                    )}

                                    <TotalComponentV2 items={[
                                        {
                                            header: t('total'),
                                            total: `${formatNumber(settlementResult.totalRevenue)} ${getCurrency()}`,
                                            icon: <MoneyIcon />
                                        }
                                    ]} />
                                </Space>

                                <Space>
                                    <div className={`${styles.statementGrid}`}>
                                            <span>
                                                {t('cost')}
                                            </span>
                                        <span>
                                                {t('attachment')}
                                            </span>
                                        <span>
                                                {t('quantity')}
                                            </span>
                                        <span>
                                                {t('total')}
                                            </span>
                                    </div>


                                    <SettlementStatementBox text={t('productCost')} amount={-settlementResult.orderLineCost} onSelect={selectEntry}  isAdmin={isAdmin} currency={getCurrency()}/>
                                    <SettlementStatementBox text={t('packagingCost')} amount={-settlementResult.totalPackagingFees} onSelect={selectEntry}  isAdmin={isAdmin} currency={getCurrency()}/>
                                    <SettlementStatementBox text={t('paymentCost')} amount={-settlementResult.totalPaymentFees} onSelect={selectEntry}  isAdmin={isAdmin} currency={getCurrency()}/>
                                    <SettlementStatementBox text={t('b2bCost')} amount={-settlementResult.totalB2BFees} onSelect={selectEntry}  isAdmin={isAdmin} currency={getCurrency()}/>
                                    {settlementResult.affiliates.map((affiliate) => (
                                        <SettlementStatementBox text={"Affiliate: " + affiliate.name} amount={-affiliate.amount} quantity={affiliate.items?.length} onSelect={selectEntry}  isAdmin={isAdmin} currency={getCurrency()}/>
                                    ))}
                                    {settlementResult.refundedAmount > 0 && (
                                        <SettlementStatementBox text={t('refunds')} amount={-settlementResult.refundedAmount} quantity={settlementResult.refundedItemCount} onSelect={selectEntry}  isAdmin={isAdmin} currency={getCurrency()}/>
                                    )}

                                    {settlementItemsByText('Expense').map(x => (
                                        <SettlementStatementBox key={x.text} text={x.text} quantity={x.quantity} amount={-(x.amount ?? 0)} items={x.items} onSelect={selectEntry}  isAdmin={isAdmin} currency={getCurrency()}/>
                                    ))}

                                    <TotalComponentV2 items={[
                                        {
                                            header: t('totalExpenses'),
                                            total: `${formatNumber(settlementResult.totalExpenses)} ${getCurrency()}`,
                                            icon: <MoneyIcon />
                                        },
                                        {
                                            header: t('totalProfit'),
                                            total: `${formatNumber(settlementResult.totalProfit)} ${getCurrency()}`,
                                            icon: <MoneyIcon />
                                        }
                                    ]} />

                                    {isAdmin && (
                                        <Space>
                                            <div>
                                                {t('split')}
                                            </div>

                                            {settlementResult.splits?.map(split => (
                                                <SettlementStatementBox key={split.name} text={split.name} amount={split.amount} onSelect={selectEntry} isAdmin={isAdmin} currency={getCurrency()}/>
                                            ))}

                                            <div>
                                                {t('distribution')}
                                            </div>

                                            {settlementResult.distribution?.map(split => (
                                                <SettlementStatementBox key={split.name} text={split.name} amount={split.amount} onSelect={selectEntry} isAdmin={isAdmin}  currency={getCurrency()}/>
                                            ))}

                                            <TotalComponentV2 items={[
                                                {
                                                    header: t('totalDistribution'),
                                                    total: `${formatNumber(settlementResult.distribution.reduce((a, b) => a + b.amount, 0))} ${getCurrency()}`,
                                                    icon: <MoneyIcon />
                                                }
                                            ]} />
                                        </Space>
                                    )}

                                </Space>

                            </Space>

                            <Space>
                                <div style={{maxHeight: 600, overflow: 'auto'}}>
                                    <Space>
                                        {settlementSales?.salesItems.map((x, index) => (
                                            <div key={x.productSku} className={`${styles.statementBox} ${styles.salesBox}`}>
                                                <div>
                                                    <Space size={"small"} direction={"horizontal"} align={"center"}>
                                                        #{index + 1}
                                                        <img width={40} height={"auto"} src={x.imageUrl} alt="" />
                                                    </Space>
                                                </div>
                                                <div>
                                                    {x.productName} {x.variantName && `| ${x.variantName}`}
                                                </div>
                                                <div>
                                                    {x.quantity - x.b2BCount} {t('B2C')} {x.b2BCount > 0 && ("| B2B: " + x.b2BCount)}
                                                </div>
                                                <div>
                                                    {`${formatNumber(x.totalSalesPriceExclVat - x.totalB2BSalesExclVat)} ${getCurrency()}`}
                                                    {x.b2BCount > 0 && (" | B2B: " + formatNumber(x.totalB2BSalesExclVat)) + `${getCurrency()}`}
                                                </div>
                                            </div>
                                        ))}
                                    </Space>
                                </div>
                            </Space>
                        </div>
                    )}
                </div>
            </PageLayout>
            <Modal visible={modalVisible} title={t('settlementEntry')} onCancel={closeManualEntryModal}>
                <SettlementEntryForm onSubmit={closeManualEntryModal} settlementEntry={selectedSettlementEntry ?? { projectId: filter['projectId'] as number}} currency={getCurrency()} />
            </Modal>
        </>
    )
}

export default SettlementResult
